import {
  Grid,
  GridCell,
  GridCellProps,
  GridColumn,
  GridContextMenuEvent,
  GridExpandChangeEvent,
  GridHeaderCellProps,
} from "@progress/kendo-react-grid";
import React, { useCallback, useEffect, useState } from "react";
import GridWrapper from "../../grid/GridWrapper";
import {
  planoAPI,
  useGetEnsembleQuery,
  useGetMagasinsQuery,
  useGetPlanoQuery,
} from "../../../redux/features/cache";
import { planoHeaders } from "../../../constante/planoHeaders";
import { formatDate } from "../../../constante/generic";
import { Planogramme } from "../../../models/Planogramme";
import { Tooltip } from "@mui/material";
import ToggleMagasin from "./ToggleMagasin";
import DetaillProduitComponent from "./DetailProduitComponent";
import { useAppDispatch } from "../../../redux/store";
import useGetPollingInterval from "../../../hooks/useGetPollingInterval";

interface DetailProps {
  prod: any;
  statut: any;
  ban: any;
  mag: any;
  setShowAddMagasin: any;
  setListMags: any;
}

interface ProductNameHeaderProps extends GridHeaderCellProps {
  children: any;
  customHeader: string;
}

function DetailComponent(props: DetailProps) {
  const { prod, statut, ban, mag, setShowAddMagasin, setListMags } = props;
  const pollingInterval = useGetPollingInterval();
  const [manquant, setManquant] = useState(false);
  const { data: planogrammes = [] } = useGetPlanoQuery(
    { prod: prod, statut: statut, ban, mag, manquant },
    { pollingInterval: pollingInterval }
  );
  const { data: ensemble = [] } = useGetEnsembleQuery(prod);
  const { data: magasins = [] } = useGetMagasinsQuery({
    banniere: "",
    treeView: false,
  });
  const [data, setData] = useState<any[]>([] as Planogramme[]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (planogrammes.length > 0) {
      const transformedGroupProduit = planogrammes.map((e) => {
        return {
          ...e,
          plano_dt_deb: formatDate(e.plano_dt_deb),
          plano_dt_fin: formatDate(e.plano_dt_fin),
          plano_trsf_dt_entree: formatDate(e.plano_trsf_dt_entree),
          plano_trsf_dt_sortie: formatDate(e.plano_trsf_dt_sortie),
        };
      });
      setData(transformedGroupProduit);
    }
  }, [planogrammes]);

  const HeaderWithTri = (
    props: ProductNameHeaderProps & { tooltip: string }
  ) => {
    let tooltipParagraphs: JSX.Element[] = [];

    if (props.tooltip !== undefined) {
      tooltipParagraphs = (props.tooltip || "")
        .split(/(?=\b\w+:)/)
        .map((segment, index) => <p key={index}>{segment.trim()}</p>);
    }
    const isEmptyTooltip =
      tooltipParagraphs.length === 1 &&
      tooltipParagraphs[0].props.children === "";

    return (
      <>
        {!isEmptyTooltip ? (
          <Tooltip title={tooltipParagraphs}>
            <span>{props.title}</span>
          </Tooltip>
        ) : props.customHeader ? (
          <ToggleMagasin
            title={props.title!}
            manquant={manquant}
            setManquant={setManquant}
          />
        ) : (
          <>
            <span>{props.title}</span> {props.children}
          </>
        )}
      </>
    );
  };

  const StatutCell = (props: GridCellProps) => {
    return (
      <td
        style={{
          color:
            props.dataItem[props.field!] === "Manquant" ||
            props.dataItem.field_highlight
              .toUpperCase()
              .split(",")
              .includes(props.field!.toUpperCase())
              ? "red"
              : "inherit",
        }}
        colSpan={1}
        className="k-table-td"
        role="gridcell"
        aria-colindex={2}
        aria-selected="false"
        data-grid-col-index="1"
      >
        {props.dataItem[props.field!]}
      </td>
    );
  };

  const IndicateurCell = (props: GridCellProps) => {
    let text = "";

    if (props.dataItem.mag_selected && props.dataItem.field_highlight)
      text = "∗ >";
    else if (props.dataItem.mag_selected) text = ">";
    else if (props.dataItem.field_highlight) text = "∗";

    return (
      <td
        colSpan={1}
        className="k-table-td"
        role="gridcell"
        aria-colindex={1}
        aria-selected="false"
        style={{ paddingBlock: "4px", paddingInline: "6px" }}
        data-grid-col-index="0"
        ref={(node) => {
          if (node && text) {
            node.style.setProperty(
              "background-color",
              props.dataItem.field_highlight ? "yellow" : "#0257d5",
              "important"
            );
            node.style.setProperty(
              "color",
              props.dataItem.field_highlight ? "black" : "white",
              "important"
            );
          }
        }}
      >
        <span style={{ fontWeight: "bold" }}>{text}</span>
      </td>
    );
  };

  const onContextMenu = (e: GridContextMenuEvent) => {
    setShowAddMagasin(e.dataItem.statut_aff === "Manquant");
    setListMags(
      e.dataItem.statut_aff === "Manquant"
        ? [magasins.find((m) => m.mag_num === e.dataItem.magasin.mag_num)]
        : []
    );
  };

  const expandChange = useCallback(
    (event: GridExpandChangeEvent) => {
      if (event.dataIndex !== -1) {
        let i = planogrammes.findIndex(
          (e) => e.plano_seq === event.dataItem.plano_seq
        );
        dispatch(
          planoAPI.util.updateQueryData(
            "getPlano",
            {
              prod: prod,
              statut: statut,
              ban,
              mag,
              manquant,
            },
            (planogramme) => {
              planogramme[i].expanded =
                planogramme[i].expanded === undefined
                  ? true
                  : !planogramme[i].expanded;
              return planogramme;
            }
          )
        );
      }
    },
    [ban, dispatch, mag, manquant, planogrammes, prod, statut]
  );

  return (
    <>
      {magasins.length > 0 && (
        <GridWrapper>
          <Grid
            data={data}
            detail={ensemble.length > 0 ? DetaillProduitComponent : null}
            expandField="expanded"
            onExpandChange={expandChange}
            reorderable={true}
            onContextMenu={onContextMenu}
            resizable
          >
            {planoHeaders.map((item: any, index: any) => (
              <GridColumn
                headerCell={(props) => (
                  <HeaderWithTri
                    {...props}
                    tooltip={item.tooltip}
                    customHeader={item.customHeader}
                  />
                )}
                field={item.champ}
                key={item.label_champ + index}
                title={item.label_champ}
                format={item.format_champ}
                headerClassName={`${
                  item.format_champ === "{0:c}" ? "alignRight" : ""
                }`}
                className={`${
                  item.format_champ === "{0:c}" ? "alignRight" : ""
                }`}
                width={item.width}
                cell={(props) => {
                  let cell =
                    item.customCell === "statutCell" ? (
                      <StatutCell {...props} />
                    ) : item.customCell === "indicateurCell" ? (
                      <IndicateurCell {...props} />
                    ) : (
                      <GridCell
                        {...props}
                        style={{
                          color: props.dataItem.field_highlight
                            .toUpperCase()
                            .split(",")
                            .includes(item.champ.toUpperCase())
                            ? "red"
                            : "black",
                        }}
                      />
                    );
                  return cell;
                }}
              />
            ))}
          </Grid>
        </GridWrapper>
      )}
    </>
  );
}

export default React.memo(DetailComponent);
